/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import Component from '@glimmer/component';
import { inject as service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import SubscriptionTopicModel from 'cio/models/subscription-topic';

import AlertsService from 'cio/services/alerts';
import AnalyticsService from 'cio/services/analytics';
import ApiErrorsService from 'cio/services/api-errors';
import FeaturesService from 'ember-feature-flags/services/features';
import { task } from 'ember-concurrency';
import AuthService from 'cio/services/auth';
import EnvironmentService from 'cio/services/environment';
import SubscriptionCenterService from 'cio/services/subscription-center';
import DS from 'ember-data';

interface TopicRowComponentArgs {
  topic: SubscriptionTopicModel;
}

export enum ActionStates {
  Closed = 'closed',
  Delete = 'delete',
}

export default class TopicRowComponent extends Component<TopicRowComponentArgs> {
  @service alerts!: AlertsService;
  @service analytics!: AnalyticsService;
  @service apiErrors!: ApiErrorsService;
  @service auth!: AuthService;
  @service environment!: EnvironmentService;
  @service store!: DS.Store;
  @service subscriptionCenter!: SubscriptionCenterService;
  @service features!: FeaturesService;

  ActionStates = ActionStates;

  @tracked selectedAction: ActionStates = ActionStates.Closed;
  @tracked openEditModal = false;

  constructor(owner: unknown, args: TopicRowComponentArgs) {
    super(owner, args);

    this.subscriptionCenter.initializeTask.perform();
  }

  get deleteTopicTooltipContent() {
    if (this.subscriptionCenter.enabledWithLastTopic) {
      return 'You have to disable subscription center first';
    }

    if (!this.args.topic.canBeDeleted) {
      return "Topics in use can't be deleted";
    }

    return 'Delete topic';
  }

  get deleteDisabled() {
    return (
      !this.auth.hasPermission('journeys/subscription_center/delete') ||
      this.deleteTask.isRunning ||
      this.subscriptionCenter.enabledWithLastTopic
    );
  }

  get topicUsed() {
    return (
      this.args.topic.usage.campaigns.length > 0 ||
      this.args.topic.usage.newsletters.length > 0 ||
      this.args.topic.usage.broadcasts.length > 0
    );
  }

  get isTopicMissingTranslations() {
    return this.subscriptionCenter.topicsMissingTranslations.includes(this.args.topic.id);
  }

  @action
  updateCancel() {
    this.openEditModal = false;
    this.args.topic.rollbackAttributes();
  }

  @action
  startDelete() {
    this.selectedAction = ActionStates.Delete;
    this.analytics.track('subscription_center', 'delete_topic_started');
  }

  deleteTask = task(async () => {
    await this.subscriptionCenter.deleteTopic(this.args.topic);
    this.selectedAction = ActionStates.Closed;
  });
}
